import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Local image imports
import headingLeftIcon from '../assets/logo png/gg.png';
import headingRightIcon from '../assets/logo png/righticon.png';
import leftDecoUrl from '../assets/logo png/rigtht side design.png';
import rightDecoUrl from '../assets/logo png/lefy side design.png';

const GameCollection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const categories = [
    'ACTION', 'STRATEGY', 'KIDS', 'EDUCATION',
    'ADVENTURE', 'PUZZLE', 'WAR', 'ARMY'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 30 + 10,
    top: Math.random() * 100,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 10 + 5,
    opacity: Math.random() * 0.3 + 0.1,
  }));

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-gradient-to-br from-white via-gray-50 to-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Animated background orbs and floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-200/40 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-200/40 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-100/30 rounded-full blur-3xl" />
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-gradient-to-r from-red-400/20 to-rose-400/20 blur-xl animate-float"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              top: `${particle.top}%`,
              left: `${particle.left}%`,
              opacity: particle.opacity,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Left decorative image – larger size */}
      <motion.div
        initial={{ opacity: 0, x: -80 }}
        animate={{ opacity: 0.9, x: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="absolute left-0 top-10 w-96 h-96 hidden md:block z-10"
      >
        <div className="relative w-full h-full">
          <div className="absolute inset-0 bg-red-300/30 rounded-full blur-3xl opacity-50" />
          <img
            src={leftDecoUrl}
            alt=""
            className="w-full h-full object-contain drop-shadow-2xl relative animate-float"
          />
        </div>
      </motion.div>

      {/* Right decorative image – larger size */}
      <motion.div
        initial={{ opacity: 0, x: 80 }}
        animate={{ opacity: 0.9, x: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="absolute right-0 bottom-10 w-96 h-96 hidden md:block z-10"
      >
        <div className="relative w-full h-full">
          <div className="absolute inset-0 bg-red-300/30 rounded-full blur-3xl opacity-50" />
          <img
            src={rightDecoUrl}
            alt=""
            className="w-full h-full object-contain drop-shadow-2xl relative animate-float"
          />
        </div>
      </motion.div>

      <div className="relative z-20 max-w-7xl mx-auto">
        {/* Heading with icons – using local imports */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="flex items-center justify-center gap-4 mb-6 flex-wrap"
        >
          <img
            src={headingLeftIcon}
            alt=""
            className="w-16 h-16 object-contain"
          />
          <h2 className="text-4xl md:text-5xl font-bold">
            <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent">
              Discover Our Game Collection
            </span>
          </h2>
          <img
            src={headingRightIcon}
            alt=""
            className="w-12 h-12 object-contain"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-center text-gray-600 text-lg max-w-2xl mx-auto mb-12"
        >
          From relaxing puzzles to thrilling adventures, find your next mobile obsession with UB Creations.
        </motion.p>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((cat, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={{ scale: 1.05, y: -2 }}
              className="px-6 py-2 bg-white/60 backdrop-blur-md rounded-full text-gray-800 font-semibold text-sm sm:text-base border border-white/30 shadow-sm hover:bg-white/80 hover:border-red-400/60 transition-all duration-300 cursor-pointer"
            >
              {cat}
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="w-24 h-0.5 bg-gradient-to-r from-red-500 to-rose-500 mx-auto my-10"
        />
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        .animate-float {
          animation: float 5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default GameCollection;